import { isString } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import { LoadingButton } from "@mui/lab";
import { Autocomplete, Avatar, Card, CardContent, IconButton, ListItemText, TextField, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";

import { useBoolean } from "src/hooks/use-boolean";

import { useTranslate } from "src/locales";
import { useGetContactByIdQuery, useUpdateContactAccountOwnerMutation } from "src/services/contacts/contacts.service";
import { User } from "src/services/organisation/organisation.types";
import { useAppSelector } from "src/store/store";

import AccountDisplay from "src/components/account-viewer/account-display";
import { ConfirmDialog } from "src/components/custom-dialog";
import Iconify from "src/components/iconify";

type Props = {
  contactId: string;
}

const AccountOwnerCard = ({ contactId }: Props) => {

  const showEditUserDialog = useBoolean();

  const { t } = useTranslate();

  const { data: contact } = useGetContactByIdQuery(contactId, {
    skip: !contactId,
  });

  const users = useAppSelector((state) => state?.data?.users);

  const [updateAccountOwner, { isLoading: isUpdatingAccountOwner }] = useUpdateContactAccountOwnerMutation();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentuser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    if (!contact) {
      setSelectedUser(null);
      setCurrentUser(null);
      return;
    }

    if (users?.length) {
      const userAccount = users.find((user) => user.id === contact.account_owner?.id);
      setSelectedUser(userAccount ?? null);
      setCurrentUser(userAccount ?? null);
    }
  }, [users, contact]);

  const handleUpdateAccountOWner = useCallback(async () => {
    if (!contact || !selectedUser) return;

    try {

      await updateAccountOwner({
        contact_id: contactId,
        data: {
          account_id: selectedUser.id as string
        },
      }).unwrap();

      enqueueSnackbar(t('contacts.api.update_account_owner.success'), { variant: 'success' });
      showEditUserDialog.onFalse();
    } catch (error) {
      enqueueSnackbar(t('contacts.api.update_account_owner.default_error'), { variant: 'error' });
    }
  }, [contact, selectedUser, showEditUserDialog, contactId, updateAccountOwner, t]);

  const handleRemoveAccountOwner = useCallback(async () => {
    if (!contact) return;

    try {
      await updateAccountOwner({
        contact_id: contactId,
        data: {
          account_id: null
        },
      }).unwrap();

      enqueueSnackbar(t('contacts.api.remove_account_owner.success'), { variant: 'success' });
      showEditUserDialog.onFalse();
    } catch (error) {
      enqueueSnackbar(t('contacts.api.remove_account_owner.default_error'), { variant: 'error' });
    }
  }, [contact, showEditUserDialog, contactId, updateAccountOwner, t]);

  return (
    <>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} display="flex" alignItems="center">
              <Avatar
                alt={currentuser ? currentuser.first_name : undefined}
                src={currentuser ? currentuser?.profile_image?.public_path : undefined}
                variant="rounded"
                sx={{ width: 48, height: 48 }}
              />

              <Stack direction="row" display="flex" width="100%" justifyContent="space-between">
                <ListItemText
                  primary={t('contacts.detail.account_owner.title')}
                  secondary={
                    currentuser ?
                      <AccountDisplay
                        accountId={currentuser.id as string}
                        textFieldProps={{
                          limit: 15,
                          text: `${currentuser.first_name} ${currentuser.last_name}`,
                          mode: 'characters',
                          showMoreEnabled: false,
                          variant: 'body2'
                        }}
                      />
                      :
                      <Typography variant="body2" color="text.secondary">
                        {t('contacts.detail.account_owner.no_account_owner')}
                      </Typography>
                  }
                />

                <Stack direction="row" spacing={1} display="flex" alignItems="center">

                  {
                    contact?.account_owner && (
                      <Tooltip title={t('contacts.detail.account_owner.remove_tooltip')} arrow placement="top">
                        <IconButton onClick={handleRemoveAccountOwner} color="error" sx={{ p: .3 }}>
                          <Iconify icon="mingcute:user-remove-fill" />
                        </IconButton>
                      </Tooltip>
                    )
                  }

                  <Tooltip title={t('contacts.detail.account_owner.update_tooltip')} arrow placement="top">
                    <IconButton onClick={showEditUserDialog.onTrue} sx={{ p: .3 }}>
                      <Iconify icon="solar:pen-bold" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {
        (contact && users?.length !== undefined) && (
          <ConfirmDialog
            open={showEditUserDialog.value}
            title={t('contacts.detail.account_owner.edit')}
            onClose={showEditUserDialog.onFalse}
            content={
              <Autocomplete
                sx={{
                  paddingTop: 2,
                }}
                multiple={false}
                options={users as User[]}
                value={selectedUser as User}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      'admin-organization.department.department_add_user.user_autocomplete.label'
                    )}
                    placeholder={t(
                      'admin-organization.department.department_add_user.user_autocomplete.placeholder'
                    )}
                  />
                )}
                onChange={(_e, value: User | null) => setSelectedUser(value)}
                renderOption={({ key, ...rest }, option: User) => (
                  <li key={key} {...rest}>
                    <Stack direction="column">
                      <Typography variant="body1">
                        {`${option.first_name} ${option.last_name}`}
                      </Typography>
                    </Stack>
                  </li>
                )}
                getOptionLabel={(option) =>
                  !isString(option) ? `${option.first_name} ${option.last_name}` : 'Assign to me'
                }
                isOptionEqualToValue={(option, value) =>
                  option && (option as User).id === (value as User).id
                }
                getOptionKey={(option) => (option as User).id as string}
              />
            }
            action={
              <LoadingButton
                variant="contained"
                disabled={!selectedUser}
                onClick={handleUpdateAccountOWner}
                loading={isUpdatingAccountOwner}>
                {t('contacts.detail.account_owner.assign')}
              </LoadingButton>
            }
          />
        )
      }

    </>
  )

}

export default AccountOwnerCard; 